import { ReportTemplate, ReportTemplateProps } from "@/components/ReportTemplate";
import { useProcessedInventoryData } from "@/hooks/useFactoryData";
import { TransferOrder, Warehouse } from "@/components/LogisticsMap";
import { useEffect, useMemo, useState } from "react";
import { 
  Factory as FactoryIcon,
  TrendingUp, 
  ShoppingCart, 
  Package
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const Factory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<TransferOrder | null>(null);
  const { isAuthenticated } = useAuth();
  
  // 从路由参数获取 search_id，默认为 1045
  const searchId = searchParams.get('id') || '1037';
  
  // 获取出入库数据
  const {
    inventoryItems,
    summary,
    charts,
    isLoading,
    error,
    refetch,
    totalCount
  } = useProcessedInventoryData(searchId);

  // 如果未认证，不加载数据
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
  }, [isAuthenticated]);

  // 模拟调拨单数据 - 基于实际仓库数据
  const mockTransferOrders: TransferOrder[] = useMemo(() => [
    {
      id: 'TO001',
      from: 'jiaheng',
      to: 'yiwu',
      status: 'in_transit',
      isCompleted: true,
      vehicle: '浙A12345',
      createTime: '2024-01-15 09:30',
      estimatedArrival: '2024-01-16 14:00',
      quantity: 500
    },
    {
      id: 'TO002',
      from: 'jinting',
      to: 'guangzhou',
      status: 'pending',
      isCompleted: false,
      vehicle: '粤B67890',
      createTime: '2024-01-15 10:15',
      estimatedArrival: '2024-01-17 16:30',
      quantity: 800
    },
    {
      id: 'TO003',
      from: 'xixiaier_haizhou',
      to: 'zhengzhou',
      status: 'in_transit',
      isCompleted: true,
      vehicle: '豫C11223',
      createTime: '2024-01-15 08:45',
      estimatedArrival: '2024-01-16 12:00',
      quantity: 300
    }
  ], []);

  // 处理仓库点击事件
  const handleWarehouseClick = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    console.log('选择仓库:', warehouse);
  };

  // 处理车辆点击事件
  const handleVehicleClick = (order: TransferOrder) => {
    setSelectedOrder(order);
    console.log('选择调拨单:', order);
  };
  
  // 使用模板配置
  const reportConfig: ReportTemplateProps = useMemo(() => {
    // 智能分析数据类型
    const dataTypes = inventoryItems.length > 0 ? Object.keys(inventoryItems[0]).filter(key => 
      key !== 'id' && key !== 'quantity' && key !== 'status' && 
      key !== 'inboundDate' && key !== 'expiryDate'
    ) : [];
    
    // 智能生成KPI统计
    const generateKPIStats = () => {
      const stats = [];
      
      // 1. 总记录数
      stats.push({
        title: "总记录数",
        value: summary?.totalCount?.toString() || "0",
        change: "当前数据",
        changeType: "neutral" as const,
        icon: Package,
        onClick: () => navigate("/factory/details")
      });
      
      // 2. 根据 tran_type 字段统计入库/出库
      if (dataTypes.includes('tran_type')) {
        const inboundCount = inventoryItems.filter(item => 
          item.tran_type && item.tran_type.includes('入库')
        ).length;
        
        stats.push({
          title: "入库记录数",
          value: inboundCount.toString(),
          change: `占比 ${summary?.totalCount ? Math.round((inboundCount / summary.totalCount) * 100) : 0}%`,
          changeType: "positive" as const,
          icon: TrendingUp,
          onClick: () => navigate("/factory/details")
        });
      }
      
      // 3. 根据数量字段统计总量
      if (dataTypes.includes('入库数量')) {
        stats.push({
          title: "累计数量",
          value: summary?.totalAmount?.toLocaleString() || "0",
          change: "总和统计",
          changeType: "positive" as const,
          icon: ShoppingCart,
          onClick: () => navigate("/factory/details")
        });
      }
      
      // 4. 根据状态字段统计效率
      if (dataTypes.includes('factory_stock_status')) {
        const passedCount = inventoryItems.filter(item => 
          item.factory_stock_status === '放行'
        ).length;
        const efficiency = summary?.totalCount ? 
          Math.round((passedCount / summary.totalCount) * 100) : 0;
          
        stats.push({
          title: "放行率",
          value: `${efficiency}%`,
          change: `${passedCount}/${summary?.totalCount || 0} 已放行`,
          changeType: efficiency >= 80 ? "positive" as const : "neutral" as const,
          icon: FactoryIcon,
          onClick: () => navigate("/factory/details")
        });
      }
      
      return stats.slice(0, 4); // 最多显示4个KPI
    };

    // 智能生成状态徽章
    const generateStatusBadges = () => {
      const badges = [];
      
      // 根据仓库统计
      if (dataTypes.includes('warehouse_name')) {
        const warehouses = [...new Set(inventoryItems.map(item => item.warehouse_name))];
        badges.push({
          label: "仓库数",
          value: `${warehouses.length}个仓库`,
          variant: "default" as const
        });
      }
      
      // 根据品牌统计
      if (dataTypes.includes('brand_name')) {
        const brands = [...new Set(inventoryItems.map(item => item.brand_name))];
        badges.push({
          label: "品牌数",
          value: `${brands.length}个品牌`,
          variant: "secondary" as const
        });
      }
      
      return badges;
    };

    return {
      title: `数据报表中心 (ID: ${searchId})`,
      subtitle: "实时数据分析与智能统计",
      icon: FactoryIcon,
      backPath: "/",
      isLoading,
      error,
      onRefresh: refetch,
      kpiStats: generateKPIStats(),
      statusInfo: {
        totalCount: totalCount || 0,
        efficiency: summary?.totalCount ? 
          Math.round((inventoryItems.filter(item => 
            item.factory_stock_status === '放行'
          ).length / summary.totalCount) * 100) : 0,
        customBadges: generateStatusBadges()
      },
      chartData: charts,
      tableData: inventoryItems,
      flowChartModule: "factory",
      logisticsMap: {
        enabled: true,
        transferOrders: mockTransferOrders,
        onWarehouseClick: handleWarehouseClick,
        onVehicleClick: handleVehicleClick
      }
    };
  }, [inventoryItems, summary, charts, isLoading, error, refetch, totalCount, navigate, searchId, mockTransferOrders]);

  return <ReportTemplate {...reportConfig} />;
};

export default Factory;
